import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Swords, ArrowLeft, Sparkles, Crown, Lock, Video, Mic } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Link } from "react-router-dom";

const CreateDebate = () => {
  const { user, token, canDebate } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [debateType, setDebateType] = useState("video"); // "video" or "voice"

  useEffect(() => {
    if (!user) {
      toast.error("Please log in to create a debate");
      navigate("/login");
      return;
    }
    
    // Don't redirect - show upgrade prompt instead
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchCategories();
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !description || !category) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/debates`, {
        title,
        description,
        category,
        debate_type: debateType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Debate created! Waiting for an opponent...");
      navigate(`/debate/${res.data.debate_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create debate");
    } finally {
      setLoading(false);
    }
  };

  // Show upgrade prompt if user doesn't have Pro subscription
  if (user && !canDebate) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6">
            <Crown className="w-10 h-10 text-accent" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Upgrade to Pro Debater</h1>
          <p className="text-muted-foreground mb-6">
            Creating debates requires a Pro Debater subscription. 
            Unlock the ability to start debates, compete on the leaderboard, and earn your debater badge.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/subscription">
              <Button className="bg-accent text-accent-foreground hover:bg-accent/90 btn-electric">
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to Pro - $9.99/mo
              </Button>
            </Link>
            <Link to="/browse">
              <Button variant="outline">Browse Debates</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-6 py-12">
        <Link 
          to="/browse" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Debates
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center">
            <Swords className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Create a Debate</h1>
            <p className="text-muted-foreground">Start a new battle and wait for a challenger</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Debate Title</Label>
            <Input
              id="title"
              data-testid="debate-title-input"
              placeholder="e.g., LeBron vs Jordan: Who's the GOAT?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-12 bg-zinc-900/50 border-white/10 focus:border-primary"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select value={category} onValueChange={setCategory} required>
              <SelectTrigger data-testid="debate-category" className="h-12 bg-zinc-900/50 border-white/10">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.category_id} value={cat.slug}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              data-testid="debate-description"
              placeholder="Describe what this debate is about and the rules..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-32 bg-zinc-900/50 border-white/10 focus:border-primary resize-none"
              required
            />
          </div>

          {/* Debate Format Selection */}
          <div className="space-y-3">
            <Label>Debate Format</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setDebateType("video")}
                className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                  debateType === "video"
                    ? "border-blue-500 bg-blue-500/10 text-blue-400"
                    : "border-white/10 bg-zinc-900/50 text-muted-foreground hover:border-white/20"
                }`}
              >
                <Video className={`w-8 h-8 ${debateType === "video" ? "text-blue-400" : ""}`} />
                <span className="font-semibold">🎥 Video Debate</span>
                <span className="text-xs text-center opacity-70">Camera + Microphone</span>
              </button>
              <button
                type="button"
                onClick={() => setDebateType("voice")}
                className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                  debateType === "voice"
                    ? "border-purple-500 bg-purple-500/10 text-purple-400"
                    : "border-white/10 bg-zinc-900/50 text-muted-foreground hover:border-white/20"
                }`}
              >
                <Mic className={`w-8 h-8 ${debateType === "voice" ? "text-purple-400" : ""}`} />
                <span className="font-semibold">🎙 Voice Debate</span>
                <span className="text-xs text-center opacity-70">Microphone Only</span>
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              This cannot be changed after the debate starts.
            </p>
          </div>

          {/* Stage Preview */}
          <div className="bg-card/30 rounded-lg border border-white/10 p-6">
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent" />
              Your Stage Preview
            </h3>
            <div className="flex items-center gap-6">
              <div className={`relative p-1 rounded-full stage-glow-${user?.stage_color || 'red'}`}>
                <div className={`w-20 h-20 rounded-full bg-${user?.stage_color === 'blue' ? '[#3b82f6]' : user?.stage_color === 'purple' ? 'purple-500' : user?.stage_color === 'green' ? 'green-500' : '[#e11d48]'} flex items-center justify-center text-2xl font-bold`}>
                  {user?.username?.[0]?.toUpperCase() || "?"}
                </div>
              </div>
              <div>
                <p className="font-bold">{user?.username || "You"}</p>
                <p className="text-sm text-muted-foreground">Red Corner (Creator)</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Stage color: {user?.stage_color || "red"}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Customize your stage color in your profile settings
            </p>
          </div>

          <Button
            type="submit"
            data-testid="create-debate-submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 font-bold uppercase tracking-wider btn-electric"
          >
            {loading ? "Creating..." : "Create Debate"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CreateDebate;
